import type React from 'react';
import { useCallback } from 'react';

import { DialogActions, DialogContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/penaltyDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import ChoosePlayerSelect from './ChoosePlayerSelect';

export interface ChoosePlayerDialogProps {
  isOpen?: boolean;
  participants?: Pick<ParticipantInterface, 'participantId' | 'participantName'>[];
  onSelect?: (
    e: React.MouseEvent<HTMLButtonElement>,
    participant: Pick<ParticipantInterface, 'participantId' | 'participantName'>,
  ) => void;
  onClose: () => void;
  onExited?: () => void;
}

export const ChoosePlayerDialog: React.FC<React.PropsWithChildren<ChoosePlayerDialogProps>> = ({
  isOpen,
  participants,
  onSelect,
  onClose,
  onExited,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>,
      participant: Pick<ParticipantInterface, 'participantId' | 'participantName'>,
    ) => {
      onSelect?.(e, participant);
    },
    [onSelect],
  );

  return (
    <TMXDialog
      className={classes.tmxDialog}
      open={isOpen}
      PaperProps={{ elevation: 0, className: classes.participantsDialog }}
      TransitionProps={{ onExited }}
    >
      <div className={classes.participantsDialogHeader}>
        <Typography className={classes.dialogTitle} variant="h2">
          {t('choosePlayerDialogTitle')}
        </Typography>
        <Typography className={classes.dialogSubtitle}>{t('choosePlayerDialogText')}</Typography>
      </div>
      <DialogContent className={classes.participantsDialogContent}>
        {participants.map((participant) => (
          <ChoosePlayerSelect
            key={participant.participantId}
            name={participant.participantName}
            participantId={participant.participantId}
            onClick={handleSelect}
          />
        ))}
      </DialogContent>
      <DialogActions className={classes.participantsDialogActions}>
        <StandardButton onClick={onClose}>{t('Close')}</StandardButton>
      </DialogActions>
    </TMXDialog>
  );
};
